import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'newhire-v2-drafts';

const buildKey = (token: string) => `${STORAGE_PREFIX}:${token}`;

interface DraftsState {
  drafts: Record<string, unknown>;
  saveDraft: (stepId: string, value: unknown) => void;
  clearDraft: (stepId: string) => void;
  clearAll: () => void;
  getDraft: <T>(stepId: string) => T | undefined;
}

const useDraftPersistence = (token: string): DraftsState => {
  const [drafts, setDrafts] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!token) return;
    try {
      const raw = localStorage.getItem(buildKey(token));
      if (raw) {
        setDrafts(JSON.parse(raw));
      } else {
        setDrafts({});
      }
    } catch {
      setDrafts({});
    }
  }, [token]);

  const persist = useCallback(
    (next: Record<string, unknown>) => {
      try {
        localStorage.setItem(buildKey(token), JSON.stringify(next));
      } catch {
        // storage may be full or disabled — drafts then live only in memory for this session
      }
    },
    [token],
  );

  const saveDraft = useCallback(
    (stepId: string, value: unknown) => {
      setDrafts((prev) => {
        const next = { ...prev, [stepId]: value };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const clearDraft = useCallback(
    (stepId: string) => {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[stepId];
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const clearAll = useCallback(() => {
    setDrafts({});
    try {
      localStorage.removeItem(buildKey(token));
    } catch {
      // ignore
    }
  }, [token]);

  const getDraft = useCallback(<T>(stepId: string): T | undefined => drafts[stepId] as T | undefined, [drafts]);

  return { drafts, saveDraft, clearDraft, clearAll, getDraft };
};

export default useDraftPersistence;
